import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Loader, Maximize2 } from 'lucide-react';

/**
 * WidgetWrapper - A resizable, collapsible container for dashboard widgets
 * 
 * Features:
 * - Starts collapsed by default (lazy loading - content only loads on first expand)
 * - Drag bottom border to resize height
 * - Saves preferences in sessionStorage (persists during session, cleared on logout/tab close)
 * - Priority widgets start expanded and load immediately
 * 
 * Session Behavior:
 * - Preferences saved during active session (page reloads, navigation)
 * - Cleared when tab is closed or user logs out
 * - Call sessionStorage.clear() on logout to clear all widget preferences
 * 
 * Props:
 * - id: Unique identifier for sessionStorage
 * - title: Widget header title
 * - size: 'full' | 'half' | 'third' | 'quarter' (grid sizing)
 * - priority: If true, widget starts expanded and loads immediately
 * - minHeight/maxHeight/defaultHeight: Height constraints in pixels
 * - onExpand: Callback triggered on first expansion (for lazy loading)
 */
const WidgetWrapper = ({ 
  id, 
  title, 
  size = 'full', 
  children, 
  onExpand,
  priority = false, // High priority widgets load immediately and start expanded
  minHeight = 200,
  maxHeight = 800,
  defaultHeight = 230
}) => {
  // Load collapse state from sessionStorage (persists during session only)
  const getInitialState = () => {
    if (priority) return false; // Priority widgets always start expanded
    const saved = sessionStorage.getItem(`widget-${id}-collapsed`);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return true; // Always start collapsed by default
  };

  // Load custom height from sessionStorage
  const getInitialHeight = () => {
    const saved = sessionStorage.getItem(`widget-${id}-height`);
    if (saved !== null) {
      return parseInt(saved, 10);
    }
    return defaultHeight;
  };

  const [collapsed, setCollapsed] = useState(getInitialState);
  const [hasLoaded, setHasLoaded] = useState(priority); // Only priority widgets load immediately
  const [isLoading, setIsLoading] = useState(false);
  const [customHeight, setCustomHeight] = useState(getInitialHeight);
  const [isResizing, setIsResizing] = useState(false);
  
  const widgetRef = useRef(null);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  // Save collapse state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(`widget-${id}-collapsed`, JSON.stringify(collapsed));
  }, [collapsed, id]);

  // Save custom height to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(`widget-${id}-height`, customHeight.toString());
  }, [customHeight, id]);

  const handleToggle = async () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);

    if (!newCollapsed && !hasLoaded) {
      setIsLoading(true);
      setHasLoaded(true);
      
      if (onExpand) {
        await onExpand();
      }
      
      setIsLoading(false);
    }
  };

  // Resize handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startYRef.current = e.clientY;
    startHeightRef.current = customHeight;
    
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeMove = (e) => {
    const deltaY = e.clientY - startYRef.current;
    const newHeight = startHeightRef.current + deltaY;
    
    // Constrain height within min/max bounds
    const constrainedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
    setCustomHeight(constrainedHeight);
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  // Reset to default height
  const handleResetHeight = (e) => {
    e.stopPropagation();
    setCustomHeight(defaultHeight);
  };

  // Get grid column span based on size
  const getColSpan = () => {
    switch (size) {
      case 'full': return 'col-span-12';
      case 'half': return 'col-span-12 md:col-span-6';
      case 'third': return 'col-span-12 md:col-span-4';
      case 'quarter': return 'col-span-12 md:col-span-3';
      default: return 'col-span-12';
    }
  };

  return (
    <div className={`${getColSpan()} ${collapsed ? '' : ''}`} ref={widgetRef}>
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-lg ${
        isResizing ? 'ring-2 ring-blue-400' : ''
      }`}>
        {/* Widget Header */}
        <div 
          className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
          onClick={handleToggle}
        >
          <h3 className="font-semibold text-gray-800 text-sm md:text-base flex items-center gap-2">
            {title}
            {priority && (
              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                Priority
              </span>
            )}
          </h3>
          
          <div className="flex items-center gap-2">
            {/* Reset height button */}
            {!collapsed && customHeight !== defaultHeight && (
              <button 
                onClick={handleResetHeight}
                className="p-1 hover:bg-white rounded-full transition-colors text-gray-500 hover:text-gray-700"
                title="Reset to default height"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            )}
            
            {/* Collapse/Expand button */}
            <button 
              className="p-1 hover:bg-white rounded-full transition-colors"
              aria-label={collapsed ? 'Expand widget' : 'Collapse widget'}
            >
              {collapsed ? (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Widget Content - RESIZABLE HEIGHT */}
        {!collapsed && (
          <div className="relative">
            <div 
              className="p-4 widget-wrapper-content overflow-y-auto overflow-x-auto"
              style={{ 
                height: `${customHeight}px`,
                transition: isResizing ? 'none' : 'height 0.2s ease'
              }}
            >
              {isLoading ? (
                // Loading skeleton
                <div className="space-y-3 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-20 bg-gray-200 rounded"></div>
                  <div className="flex justify-center items-center py-4">
                    <Loader className="w-6 h-6 text-blue-600 animate-spin" />
                    <span className="ml-2 text-sm text-gray-600">Loading...</span>
                  </div>
                </div>
              ) : hasLoaded ? (
                children
              ) : (
                // Initial placeholder
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">Click to load widget data</p>
                </div>
              )}
            </div>

            {/* Resize Handle */}
            <div 
              className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize group hover:bg-blue-50 transition-colors flex items-center justify-center"
              onMouseDown={handleResizeStart}
              title="Drag to resize"
            >
              <div className="w-12 h-1 bg-gray-300 rounded-full group-hover:bg-blue-400 transition-colors"></div>
            </div>

            {/* Resize indicator */}
            {isResizing && (
              <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded shadow-lg">
                {customHeight}px
              </div>
            )}
          </div>
        )}

        {/* Collapsed Preview */}
        {collapsed && (
          <div className="px-4 py-2 text-xs text-gray-500 italic border-t border-gray-100 bg-gray-50">
            Click to expand and load widget content
          </div>
        )}
      </div>
    </div>
  );
};

// Utility function to clear all widget preferences (call this on logout)
export const clearWidgetPreferences = () => {
  // Get all keys from sessionStorage
  const keys = Object.keys(sessionStorage);
  
  // Remove all widget-related items
  keys.forEach(key => {
    if (key.startsWith('widget-')) {
      sessionStorage.removeItem(key);
    }
  });
  
  console.log('Widget preferences cleared');
};

export default WidgetWrapper;